import { useState, useContext } from 'react'
import AuthContext from 'contexte/AuthContext'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import Badge from 'components/Badge/Badge'
import TickerImage from 'components/DataComponents/TickerImage/TickerImage'
import { launchError, launchSuccess } from 'components/notifications'
import { formatNumberWithSpaces } from 'utils/numbers'
import ConfirmModal from 'components/ConfirmModal/ConfirmModal'
import BBookEmpty from './BBookEmpty'
import BBookInfo from './BBookInfo'
import BBookListToolbar, { BBookPager } from './BBookListToolbar'
import { money, signedMoney } from '../report/pnlCharts'
import { leverageLabel } from '../leverage'
import { usePolling, useT } from '../hooks'
import { recall, remember } from '../cache'
import { sortedHead, sortFromHead } from '../sorting'
import { fetchBBookReturnablePositions, returnPositionToBBook } from 'services/bBookServices'

const REFRESH_MS = 15000
const SIDE_BADGE = { LONG: '#0F766E', SHORT: '#C2410C' } // darker than the shared badge colours, so white text reaches 4.5:1
const COLUMNS = ['user', 'ticker', 'side', 'leverage', 'invested', 'quantity', 'market_value', 'client_pl', 'funded', 'action']
const SORTABLE = COLUMNS.filter((key) => key !== 'action')

// Positions open on the real book (A) that the house could take back: the reverse of "Forcer en A-Book", with the client's live gain to judge
// whether it is worth it. The desk asks, Bbook-LP and the pairing
// lambda re-check the house's controls and do the move; an answer here only says the request was heard.
const BBookReturnable = ({ refreshKey = 0, onMoved }) => {
  const { t } = useT()
  const { user } = useContext(AuthContext)
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(10)
  const [sort, setSort] = useState(null) // null = biggest cost basis first
  const [filters, setFilters] = useState({ side: null, ticker: '' })
  const setFilter = (key) => (value) => { setFilters((prev) => ({ ...prev, [key]: value })); setPage(0) }
  const filtering = !!(filters.side || filters.ticker)
  const [positions, setPositions] = useState(() => recall('moves:returnable') ?? [])
  const [at, setAt] = useState(null)
  const [stale, setStale] = useState(false)
  const [toReturn, setToReturn] = useState(null)
  const [returning, setReturning] = useState(false)

  usePolling(async () => {
    try {
      setPositions(remember('moves:returnable', await fetchBBookReturnablePositions(user, page, limit, sort, filters)))
      setAt(new Date())
      setStale(false)
    } catch (e) {
      if (!at) launchError(t('notifications.b_book.returnable.fetch_error'))
      setStale(true)
    }
  }, REFRESH_MS, [user, page, limit, sort, filters, refreshKey]) // refreshKey: the parent asks for a sooner poll after a move

  const columnLabel = (key) => t(`pages.b_book.returnable.table.${key}`)
  const onHeaderClick = (clicked) => { setSort(sortFromHead(clicked, COLUMNS, columnLabel, sort, SORTABLE)); setPage(0) }

  const confirmReturn = async () => {
    const position_id = toReturn?.position_id
    try {
      setReturning(true)
      await returnPositionToBBook(user, position_id)
      setPositions(prev => prev.filter((position) => position.position_id !== position_id))
      launchSuccess(t('notifications.b_book.returnable.success'))
      onMoved?.()
    } catch (e) {
      const status = e?.response?.status
      launchError(t(status === 503 ? 'notifications.b_book.engine_not_listening' : status === 409 ? 'notifications.b_book.returnable.not_returnable' : 'notifications.b_book.returnable.error'))
    } finally {
      setReturning(false)
      setToReturn(null)
    }
  }

  const tbody = (positions || []).map((item) => ({
    data: [
      `${item.firstName} ${item.lastName}`,
      <TickerImage ticker={item.ticker} />,
      <Badge color={SIDE_BADGE[String(item.side).toUpperCase()]} text={String(item.side).toUpperCase()} />,
      <span className='b_book_num'>{Number(item.leverage_rate) > 0 ? leverageLabel(Number(item.leverage_rate)) : '—'}</span>,
      <span className='b_book_num'>{item.invested_eur === null || item.invested_eur === undefined ? '—' : money(item.invested_eur)}</span>,
      <span className='b_book_num'>{formatNumberWithSpaces(item.qty)}</span>,
      <span className='b_book_num'>{item.market_value === null || item.market_value === undefined ? '—' : money(item.market_value)}</span>,
      item.client_unrealized_pl === null || item.client_unrealized_pl === undefined ? '—' : (
        <span className='b_book_stack'>
          <span className={`b_book_num ${item.client_unrealized_pl > 0 ? 'b_book_position_gain' : item.client_unrealized_pl < 0 ? 'b_book_position_loss' : ''}`}>
            {signedMoney(item.client_unrealized_pl)}
          </span>
          {item.client_unrealized_pct !== null && <span className='b_book_cell_hint'>{t('pages.b_book.returnable.client_pl_pct', { pct: item.client_unrealized_pct.toLocaleString('fr-FR', { maximumFractionDigits: 1, signDisplay: 'always' }) })}</span>}
        </span>
      ),
      <span className='b_book_num'>{item.funded_margin_eur === null || item.funded_margin_eur === undefined ? '—' : item.funded_margin_eur > 0 ? money(item.funded_margin_eur) : '0'}</span>,
      <button className='b_book_row_button' onClick={(e) => { e.stopPropagation(); setToReturn(item) }}
        aria-label={t('pages.b_book.returnable.action_label', { ticker: item.ticker, user: `${item.firstName} ${item.lastName}` })}>
        {t('pages.b_book.returnable.action')}
      </button>
    ],
    utils: { id: item.position_id }
  }))

  return (
    <section aria-labelledby='b-book-returnable-title'>
      <div className='b_book_title_component'>
        <h2 id='b-book-returnable-title'>{t('pages.b_book.returnable.title')}</h2>
        <BBookInfo label={t('pages.b_book.returnable.title')} text={t('pages.b_book_info.returnable')} />
        {at && (
          <span className={`b_book_live_stamp ${stale ? 'b_book_live_stamp_stale' : ''}`}>
            <span className='b_book_live_dot' aria-hidden='true' />
            {stale ? t('pages.b_book_pnl.live.stale') : t('pages.b_book_pnl.live.updated', { time: at.toLocaleTimeString('fr-FR') })}
          </span>
        )}
      </div>
      <p className='b_book_hint'>{t('pages.b_book.returnable.hint')}</p>

      <div className='b_book_card'>
        {(tbody.length > 0 || page > 0 || filtering) && (
          <BBookListToolbar searchLabel={t('pages.b_book.filters.ticker')} searchPlaceholder={t('pages.b_book.filters.ticker_placeholder')}
            search={filters.ticker} setSearch={setFilter('ticker')} side={filters.side} setSide={setFilter('side')} limit={limit} setLimit={(value) => { setLimit(value); setPage(0) }} />
        )}
        {tbody.length || page > 0 ? (
          <div className='table-container b_book_sortable'>
            <div className='table-column'>
              <Tableau
                thead={sortedHead(COLUMNS, columnLabel, sort)}
                tbody={tbody}
                onHeaderClick={onHeaderClick}
              />
            </div>
            <BBookPager page={page} hasNext={tbody.length >= limit} onChange={setPage} />
          </div>
        ) : (
          <BBookEmpty title={t(filtering ? 'pages.b_book.filters.empty' : 'pages.b_book.returnable.empty')} hint={filtering ? null : t('pages.b_book.returnable.empty_hint')} />
        )}
      </div>

      <ConfirmModal
        isOpen={!!toReturn}
        loading={returning}
        danger={false}
        title={t('pages.b_book.returnable.confirm_title')}
        message={toReturn ? t('pages.b_book.returnable.confirm_message', {
          user: `${toReturn.firstName} ${toReturn.lastName}`,
          side: toReturn.side,
          qty: toReturn.qty,
          ticker: toReturn.ticker,
        }) : ''}
        confirmLabel={t('pages.b_book.returnable.confirm')}
        cancelLabel={t('pages.b_book.returnable.cancel')}
        onConfirm={confirmReturn}
        onClose={() => setToReturn(null)}
      />
    </section>
  )
}

export default BBookReturnable
